import React, { PropsWithChildren, ReactNode, Reducer, useContext, useReducer } from 'react'

import { Notifications } from '@app/common/services/Notifications/Notifications'
import { AlertType } from '@app/lib/display/Alert/Alert'

export { AlertType }

export type Notification = {
  id: string
  type: AlertType
  message: ReactNode
  timeout?: number
}

type NotificationsProviderValue = {
  notifications: Array<Notification>
  pushNotification: (alert: Omit<Notification, 'id'>) => void
  dismissNotification: (id: string) => void
}

const NotificationsContext = React.createContext<NotificationsProviderValue>(
  {} as NotificationsProviderValue
)

const generateRandomId = () => {
  return Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substring(2, 10)
}

type AddAction = {
  type: 'add'
  payload: Notification
}

type DismissAction = {
  type: 'dismiss'
  payload: Pick<Notification, 'id'>
}

const reducer: Reducer<Array<Notification>, AddAction | DismissAction> = (
  state,
  { type, payload }
) => {
  switch (type) {
    case 'add':
      return [...state, payload]

    case 'dismiss': {
      return state.filter((e) => e.id !== payload.id)
    }

    default: {
      throw new Error()
    }
  }
}

export const NotificationsProvider = ({ children }: PropsWithChildren<any>) => {
  const [notifications, dispatch] = useReducer(reducer, [])

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        pushNotification: (alert) =>
          dispatch({
            type: 'add',
            payload: {
              id: generateRandomId(),
              timeout: alert.type !== AlertType.error ? 30000 : undefined,
              ...alert
            }
          }),
        dismissNotification: (id) => {
          dispatch({
            type: 'dismiss',
            payload: { id }
          })
        }
      }}
    >
      {children}
      <Notifications />
    </NotificationsContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationsContext)

  if (!context) {
    throw new Error('Please wrap the app with NotificationsProvider')
  }

  return context
}
